/**
 * ComplaintDetailPage — Issue #175.
 *
 * Admin detail view for a complaint / dispute work order.
 *
 * 红线：
 *   - 所有 mutation 必须使用后端返回值更新本地 state；不允许前端假 status。
 *   - 升级和复盘要求非空原因，前端做最小校验，最终由后端再次校验。
 *   - 显示完整 audit event 流水。
 */
import { useEffect, useMemo, useState } from 'react';
import { useLocation, useRoute } from 'wouter';
import { toast } from 'sonner';
import AccessDenied from '@/components/AccessDenied';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { useAuth } from '@/hooks/useAuth';
import { cn } from '@/lib/utils';
import {
  COMPLAINT_PERMISSIONS,
  hasPermission,
} from '@/lib/complaintPermissions';
import {
  useComplaints,
  COMPLAINT_RISK_LABELS,
  COMPLAINT_STATE_LABELS,
  COMPLAINT_TYPE_LABELS,
  type ComplaintWorkOrderDto,
  type ComplaintWorkOrderEventDto,
} from '@/hooks/useComplaints';

const BADGE_BASE = 'inline-flex rounded-full px-2 py-0.5 text-xs font-medium';

function fmt(value?: string | null): string {
  return value ? new Date(value).toLocaleString('zh-CN') : '—';
}

interface ReasonModalProps {
  title: string;
  confirmLabel: string;
  confirmTone: string;
  value: string;
  placeholder?: string;
  onChange: (v: string) => void;
  onCancel: () => void;
  onConfirm: () => Promise<void> | void;
  busy?: boolean;
}

function ReasonModal({
  title,
  confirmLabel,
  confirmTone,
  value,
  placeholder,
  onChange,
  onCancel,
  onConfirm,
  busy,
}: ReasonModalProps) {
  const enabled = value.trim().length > 0 && !busy;
  return (
    <div
      role="dialog"
      aria-modal="true"
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40"
    >
      <div className="w-full max-w-lg rounded bg-white p-4 shadow-lg">
        <h2 className="text-lg font-semibold">{title}</h2>
        <textarea
          className="mt-3 min-h-[120px] w-full rounded border border-slate-300 px-2 py-1 text-sm"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
        />
        <div className="mt-3 flex justify-end gap-2">
          <button
            type="button"
            className="rounded border border-slate-300 px-3 py-1 text-sm hover:bg-slate-50"
            onClick={onCancel}
          >
            取消
          </button>
          <button
            type="button"
            disabled={!enabled}
            className={cn(
              'rounded px-3 py-1 text-sm text-white disabled:opacity-50',
              confirmTone,
            )}
            onClick={() => {
              if (enabled) void onConfirm();
            }}
          >
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function ComplaintDetailPage() {
  const { permissions } = useAuth();
  const canRead = hasPermission(permissions, COMPLAINT_PERMISSIONS.READ);
  const canTransition = hasPermission(permissions, COMPLAINT_PERMISSIONS.TRANSITION);
  const canEscalate = hasPermission(permissions, COMPLAINT_PERMISSIONS.ESCALATE);
  const canReview = hasPermission(permissions, COMPLAINT_PERMISSIONS.REVIEW);
  const canClose = hasPermission(permissions, COMPLAINT_PERMISSIONS.CLOSE);

  const [, navigate] = useLocation();
  const [, params] = useRoute('/complaints/:id');
  const id = params?.id ?? '';

  const {
    fetchDetail,
    fetchEvents,
    acceptComplaint,
    startComplaint,
    escalateComplaint,
    submitForReview,
    reviewComplaint,
    closeComplaint,
    rejectComplaint,
    error,
  } = useComplaints();

  const [row, setRow] = useState<ComplaintWorkOrderDto | null>(null);
  const [events, setEvents] = useState<ComplaintWorkOrderEventDto[]>([]);
  const [loading, setLoading] = useState(true);
  const [busyAction, setBusyAction] = useState<string | null>(null);

  const [showEscalate, setShowEscalate] = useState(false);
  const [escalateReason, setEscalateReason] = useState('');

  const [showReview, setShowReview] = useState(false);
  const [reviewConclusion, setReviewConclusion] = useState('');

  const [showClose, setShowClose] = useState(false);
  const [closeReason, setCloseReason] = useState('');

  const [showReject, setShowReject] = useState(false);
  const [rejectReason, setRejectReason] = useState('');

  useEffect(() => {
    if (!canRead || !id) return;
    let cancelled = false;
    void (async () => {
      setLoading(true);
      const [detail, log] = await Promise.all([
        fetchDetail(id),
        fetchEvents(id),
      ]);
      if (cancelled) return;
      setRow(detail);
      if (log) setEvents(log);
      setLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [canRead, id, fetchDetail, fetchEvents]);

  useEffect(() => {
    if (error) {
      toast.error(error.message);
    }
  }, [error]);

  const sortedEvents = useMemo(() => {
    return [...events].sort(
      (a, b) =>
        new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime(),
    );
  }, [events]);

  async function refreshAfter(next: ComplaintWorkOrderDto | null) {
    if (!next) return;
    setRow(next);
    const log = await fetchEvents(next.id);
    if (log) setEvents(log);
  }

  if (!canRead) {
    return <AccessDenied resource="投诉与纠纷工单" />;
  }

  if (loading) {
    return <div className="p-6 text-sm text-muted-foreground">加载中…</div>;
  }

  if (!row) {
    return (
      <div className="space-y-3 p-6">
        <div className="rounded border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
          未找到该投诉记录或无访问权限。
        </div>
        <Button variant="ghost" onClick={() => navigate('/complaints')}>
          返回列表
        </Button>
      </div>
    );
  }

  const status = row.status;
  const canAccept = canTransition && status === 'submitted';
  const canStart = canTransition && status === 'accepted';
  const canEscalateNow =
    canEscalate && (status === 'accepted' || status === 'processing');
  const canSubmitReview =
    canTransition && (status === 'processing' || status === 'escalated');
  const canReviewNow = canReview && status === 'pending_review';
  const canCloseDirect =
    canClose &&
    (status === 'processing' || status === 'escalated');
  const canRejectNow = canClose && (status === 'submitted' || status === 'accepted');

  async function withBusy<T>(action: string, fn: () => Promise<T>): Promise<T> {
    setBusyAction(action);
    try {
      return await fn();
    } finally {
      setBusyAction(null);
    }
  }

  return (
    <div className="space-y-4 p-6">
      <div className="flex items-center gap-2 text-sm text-muted-foreground">
        <a href="/complaints" className="hover:underline">
          投诉与纠纷工单
        </a>
        <span>/</span>
        <span className="text-foreground">{row.id}</span>
      </div>

      <header className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold">{row.title}</h1>
          <p className="mt-1 text-sm text-slate-500">
            类型 {COMPLAINT_TYPE_LABELS[row.complaintType]} · 风险{' '}
            {COMPLAINT_RISK_LABELS[row.riskLevel]} · 当前状态{' '}
            <span className={cn(BADGE_BASE, 'bg-slate-100 text-slate-700')}>
              {COMPLAINT_STATE_LABELS[row.status]}
            </span>
          </p>
        </div>
        <div className="text-xs text-slate-500">v{row.version}</div>
      </header>

      {row.escalated ? (
        <div className="rounded border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
          已升级：原因 “{row.escalationReason}”，升级人 {row.escalatedBy ?? '—'}，
          时间 {fmt(row.escalatedAt)}
        </div>
      ) : null}

      <Card className="p-4">
        <h2 className="text-sm font-semibold text-slate-700">基本信息</h2>
        <dl className="mt-3 grid grid-cols-2 gap-x-6 gap-y-2 text-sm">
          <Field label="客户 ID" value={row.customerId} mono />
          <Field label="订单 ID" value={row.orderId ?? '—'} mono />
          <Field label="订单项目 ID" value={row.orderProjectId ?? '—'} mono />
          <Field label="服务用户 ID" value={row.serviceUserId ?? '—'} mono />
          <Field label="处理人" value={row.assigneeId ?? '—'} mono />
          <Field label="负责人" value={row.ownerId ?? '—'} mono />
          <Field label="SLA 到期" value={fmt(row.slaDueAt)} />
          <Field label="创建时间" value={fmt(row.createdAt)} />
        </dl>
        <div className="mt-4 space-y-2 text-sm">
          <div>
            <div className="text-xs text-slate-500">客户诉求</div>
            <div className="whitespace-pre-wrap">{row.customerDemand}</div>
          </div>
          {row.description ? (
            <div>
              <div className="text-xs text-slate-500">内部备注</div>
              <div className="whitespace-pre-wrap">{row.description}</div>
            </div>
          ) : null}
          {row.reviewConclusion ? (
            <div>
              <div className="text-xs text-slate-500">复盘结论</div>
              <div className="whitespace-pre-wrap">{row.reviewConclusion}</div>
              <div className="mt-1 text-xs text-slate-500">
                复盘人 {row.reviewedBy ?? '—'} · {fmt(row.reviewedAt)}
              </div>
            </div>
          ) : null}
          {row.closeReason ? (
            <div>
              <div className="text-xs text-slate-500">闭环原因</div>
              <div className="whitespace-pre-wrap">{row.closeReason}</div>
              <div className="mt-1 text-xs text-slate-500">
                闭环人 {row.closedBy ?? '—'} · {fmt(row.closedAt)}
              </div>
            </div>
          ) : null}
        </div>
      </Card>

      <Card className="p-4">
        <h2 className="text-sm font-semibold text-slate-700">操作</h2>
        <div className="mt-3 flex flex-wrap gap-2">
          {canAccept ? (
            <Button
              size="sm"
              disabled={busyAction !== null}
              onClick={async () => {
                const next = await withBusy('accept', () =>
                  acceptComplaint(row.id, { expectedVersion: row.version }),
                );
                if (next) {
                  toast.success('已受理');
                  await refreshAfter(next);
                }
              }}
            >
              受理
            </Button>
          ) : null}
          {canStart ? (
            <Button
              size="sm"
              disabled={busyAction !== null}
              onClick={async () => {
                const next = await withBusy('start', () =>
                  startComplaint(row.id, { expectedVersion: row.version }),
                );
                if (next) {
                  toast.success('已开始处理');
                  await refreshAfter(next);
                }
              }}
            >
              开始处理
            </Button>
          ) : null}
          {canEscalateNow ? (
            <Button
              size="sm"
              variant="destructive"
              disabled={busyAction !== null}
              onClick={() => {
                setEscalateReason('');
                setShowEscalate(true);
              }}
            >
              升级
            </Button>
          ) : null}
          {canSubmitReview ? (
            <Button
              size="sm"
              variant="secondary"
              disabled={busyAction !== null}
              onClick={async () => {
                const next = await withBusy('submit_review', () =>
                  submitForReview(row.id, { expectedVersion: row.version }),
                );
                if (next) {
                  toast.success('已提交复盘');
                  await refreshAfter(next);
                }
              }}
            >
              提交复盘
            </Button>
          ) : null}
          {canReviewNow ? (
            <Button
              size="sm"
              disabled={busyAction !== null}
              onClick={() => {
                setReviewConclusion('');
                setShowReview(true);
              }}
            >
              完成复盘
            </Button>
          ) : null}
          {canCloseDirect ? (
            <Button
              size="sm"
              variant="outline"
              disabled={busyAction !== null}
              onClick={() => {
                setCloseReason('');
                setShowClose(true);
              }}
            >
              直接闭环
            </Button>
          ) : null}
          {canRejectNow ? (
            <Button
              size="sm"
              variant="outline"
              disabled={busyAction !== null}
              onClick={() => {
                setRejectReason('');
                setShowReject(true);
              }}
            >
              判定无效
            </Button>
          ) : null}
          {busyAction ? (
            <span className="self-center text-xs text-slate-500">提交中…</span>
          ) : null}
        </div>
      </Card>

      <Card className="p-4">
        <h2 className="text-sm font-semibold text-slate-700">审计事件</h2>
        {sortedEvents.length === 0 ? (
          <div className="mt-3 text-sm text-muted-foreground">暂无事件</div>
        ) : (
          <ol className="mt-3 space-y-2 text-sm">
            {sortedEvents.map((event) => (
              <li
                key={event.id}
                className="rounded border border-slate-200 px-3 py-2"
              >
                <div className="flex items-center justify-between text-xs text-slate-500">
                  <span>
                    {event.action} · {event.fromState ?? '∅'} → {event.toState}
                  </span>
                  <span>{fmt(event.createdAt)}</span>
                </div>
                <div className="mt-1 text-xs text-slate-500">
                  操作人 {event.actorType} / {event.actorId}
                </div>
                {event.note ? (
                  <div className="mt-1 whitespace-pre-wrap text-sm">{event.note}</div>
                ) : null}
              </li>
            ))}
          </ol>
        )}
      </Card>

      {showEscalate ? (
        <ReasonModal
          title="升级投诉"
          confirmLabel="确认升级"
          confirmTone="bg-red-600 hover:bg-red-700"
          value={escalateReason}
          placeholder="必填：升级原因（高风险、客户强烈不满、SLA 超时等）"
          busy={busyAction === 'escalate'}
          onChange={setEscalateReason}
          onCancel={() => setShowEscalate(false)}
          onConfirm={async () => {
            const trimmed = escalateReason.trim();
            if (trimmed.length === 0) return;
            const next = await withBusy('escalate', () =>
              escalateComplaint(row.id, {
                expectedVersion: row.version,
                reason: trimmed,
              }),
            );
            if (next) {
              toast.success('已升级，已写入审计事件');
              setShowEscalate(false);
              await refreshAfter(next);
            }
          }}
        />
      ) : null}

      {showReview ? (
        <ReasonModal
          title="复盘结论"
          confirmLabel="完成复盘并闭环"
          confirmTone="bg-blue-600 hover:bg-blue-700"
          value={reviewConclusion}
          placeholder="必填：复盘结论将写入工单和审计事件，并将状态推进到已闭环。"
          busy={busyAction === 'review'}
          onChange={setReviewConclusion}
          onCancel={() => setShowReview(false)}
          onConfirm={async () => {
            const trimmed = reviewConclusion.trim();
            if (trimmed.length === 0) return;
            const next = await withBusy('review', () =>
              reviewComplaint(row.id, {
                expectedVersion: row.version,
                conclusion: trimmed,
              }),
            );
            if (next) {
              toast.success('复盘已记录，工单已闭环');
              setShowReview(false);
              await refreshAfter(next);
            }
          }}
        />
      ) : null}

      {showClose ? (
        <ReasonModal
          title="直接闭环"
          confirmLabel="确认闭环"
          confirmTone="bg-green-600 hover:bg-green-700"
          value={closeReason}
          placeholder="必填：闭环原因"
          busy={busyAction === 'close'}
          onChange={setCloseReason}
          onCancel={() => setShowClose(false)}
          onConfirm={async () => {
            const trimmed = closeReason.trim();
            if (trimmed.length === 0) return;
            const next = await withBusy('close', () =>
              closeComplaint(row.id, {
                expectedVersion: row.version,
                closeReason: trimmed,
              }),
            );
            if (next) {
              toast.success('已闭环');
              setShowClose(false);
              await refreshAfter(next);
            }
          }}
        />
      ) : null}

      {showReject ? (
        <ReasonModal
          title="判定为无效投诉"
          confirmLabel="确认判无效"
          confirmTone="bg-slate-600 hover:bg-slate-700"
          value={rejectReason}
          placeholder="必填：判定无效的原因"
          busy={busyAction === 'reject'}
          onChange={setRejectReason}
          onCancel={() => setShowReject(false)}
          onConfirm={async () => {
            const trimmed = rejectReason.trim();
            if (trimmed.length === 0) return;
            const next = await withBusy('reject', () =>
              rejectComplaint(row.id, {
                expectedVersion: row.version,
                rejectReason: trimmed,
              }),
            );
            if (next) {
              toast.success('已判定为无效投诉');
              setShowReject(false);
              await refreshAfter(next);
            }
          }}
        />
      ) : null}
    </div>
  );
}

function Field({
  label,
  value,
  mono,
}: {
  label: string;
  value: string;
  mono?: boolean;
}) {
  return (
    <>
      <dt className="text-xs text-slate-500">{label}</dt>
      <dd className={cn(mono ? 'font-mono text-xs' : 'text-sm')}>{value}</dd>
    </>
  );
}
